import React, {FC, useState} from "react";
import {useNavigate, useParams} from "react-router";
import {ValidationError} from "../api/api";
import {useApiService} from "@traas/lib/src/api/hooks/useApiService";
import config from "../config";
import {getToken} from "@ventumsoftware/react-toolkit";
import {useApi, useFetchedResource} from "../api/APIContext";
import {useRefreshEffect} from "@traas/lib/src/realtime/RefreshController";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faChevronRight,
  faEllipsisVertical,
  faPencil, faTimes, faTriangleExclamation,
  faUser
} from "@fortawesome/free-solid-svg-icons";
import {Employee} from "@traas/lib/src/api/dto/Employee";
import {PersoneelContextMenu} from "../components/Personeel/PersoneelContextMenu";
import moment from "moment";
import {TabContainer} from "@traas/lib/src/components/layout/TabContainer";
import {faHardHat, faStarOfLife, faUserPlus} from "@fortawesome/pro-solid-svg-icons";
import {EmployeeEmergencyCard} from "../components/Personeel/EmergencyCards/EmployeeEmergencyCard";
import {ContactEmergencyCard} from "../components/Personeel/EmergencyCards/ContactEmergencyCard";
import {GpEmergencyCard} from "../components/Personeel/EmergencyCards/GpEmergencyCard";
import {RoosterEditor} from "../personel/components/RoosterEditor";
import {RoleEditor} from "../personel/components/RoleEditor";
import {usePermissions} from "../auth/permissions";
import {Card} from "@traas/lib/src/components/Card";
import {useModal} from "../../../lib/src/components/layout/ModalProvider";
import {EditEmployeeBSNModal} from "../modals/EditEmployeeBSNModal";
import {UpdateEmployeeModal} from "../modals/UpdateEmployeeModal";
import {DisableLoginModal} from "../modals/DisableLoginModal";
import {EditEmployeeTypeModal} from "../modals/EditEmployeeTypeModal";
import {EditEmployeeContractModal} from "../modals/EditEmployeeContractModal";
import {EditEmployeeRolesModal} from "../modals/EditEmployeeRolesModal";
import {Toggle} from "@traas/lib/src/components/form/Toggle";
import {Button} from "@traas/lib/src/components/form/Button";
import {OverrideRoosterEditor} from "../personel/components/OverrideRoosterEditor";

export const PersoneelDetails: FC = () => {
  const { employee } = useParams()
  const navigate = useNavigate()
  const { employeeService } = useApiService(config, getToken, navigate)
  const { resource: employeeResource, reload: reloadEmployee } = useFetchedResource((employee: string) => employeeService.read(employee))

  useRefreshEffect('employee', () => {
    if (employee !== undefined) {
      reloadEmployee(employee)
    }
  }, [employee])

  return <main className={"flex-1 overflow-y-scroll max-h-[calc(100vh-4rem)]"}>
    <div className={'mx-6 my-8'}>
      <div className={'max-w-(--breakpoint-xl) mx-auto'}>
        {employeeResource === null ? <>
          <div>
            Kan werknemer niet inladen of er is geen bestaande werknemer met dit ID.
          </div>
        </> : <div className={''}>
          <PageHeader employee={employeeResource} onUpdated={() => employee ? reloadEmployee(employee) : null} />
          <EmployeeContent employee={employeeResource} />
        </div>
        }
      </div>
    </div>
  </main>
}

const PageHeader: FC<{ employee: Employee, onUpdated: () => void }> = (props) => {
  const navigate = useNavigate()
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true)
  return <div className={"flex-1 mb-4 space-y-3"}>
    <div className={"flex items-center justify-between h-12"}>

      <div className={"flex items-center space-x-3 text-xl font-bold"}>
        <div className={"hover:underline cursor-pointer text-brand-900"} onClick={() => navigate(`/personeel`)}>Personeel</div>
        <FontAwesomeIcon icon={faChevronRight} className={'text-lg'} />
        <div className={"text-slate-900"}>{props.employee.firstName} {props.employee.lastName}</div>
      </div>

      <div className={"relative"}>
        <div className={"flex items-center bg-white border border-slate-200 rounded-sm px-2 py-2 hover:bg-slate-50 hover:cursor-pointer"} onClick={() => setIsCollapsed(false)}>
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </div>
        {!isCollapsed && <div className={"fixed inset-0 z-30 bg-[#0001] dark:bg-[#0002]"} onClick={() => setIsCollapsed(true)}/>}
        {!isCollapsed && <PersoneelContextMenu employee={props.employee} close={() => setIsCollapsed(true)} onUpdated={props.onUpdated} />}
      </div>
    </div>
  </div>
}

const EmployeeContent: FC<{ employee: Employee }> = (props) => {
  const {canEditEmployeesRoles} = usePermissions()
  return <>
    <div className={'grid grid-cols-2 gap-8'}>

      {/* General Info */}
      <EmployeeInfo employee={props.employee} />
      <EmployeeSettings employee={props.employee} />
      <EmployeeRooster employee={props.employee} />
      <EmergencyDetailsCards employee={props.employee} />
    </div>
  </>
}

const EmployeeInfo: FC<{ employee: Employee }> = ({ employee }) => {
  const { canEditEmployeesRoles } = usePermissions()

  const editEmployeeNameModal = useModal({title: 'Wijzig naam', body: <UpdateEmployeeModal employee={employee} updateName={true} updateEmail={false} updatePassword={false} />})
  const changeEmailModal = useModal({title: 'Email wijzigen', body: <UpdateEmployeeModal employee={employee} updateName={false} updateEmail={true} updatePassword={false} />})
  const activateLoginModal = useModal({title: 'Activeer medewerker login', body: <UpdateEmployeeModal employee={employee} updateName={false} updateEmail={true} updatePassword={true} />})
  const disableLoginModal = useModal({title: 'Login intrekken', body: <DisableLoginModal employee={employee} />})
  const editEmployeeBsnModal = useModal({title: 'Wijzig BSN', body: <EditEmployeeBSNModal employee={employee} />})
  const editContractPeriodModal = useModal({title: 'Wijzig Contract', body: <EditEmployeeContractModal employee={employee} />})
  const editEmployeeTypeModal = useModal({title: 'Wijzig type', body: <EditEmployeeTypeModal employee={employee} />})
  const editEmployeeRoles = useModal({title: 'Rollen aanpassen', body: <EditEmployeeRolesModal employee={employee} />, size:'md'})

  return <div className={'-my-8'}>
    <Card title={"Algemene informatie"} >
      <div className={'grid grid-cols-5 gap-4 items-center'}>
        {/* Naam */}
        <div className={'col-span-2'}>Naam:</div>
        <div className={'flex flex-row items-center space-x-2 col-span-2'}>
          <div>{employee.firstName} {employee.lastName}</div>
        </div>
        <div className={'flex col-span-1 justify-end hover:cursor-pointer'} onClick={() => editEmployeeNameModal.open()}>
          <div className={"flex justify-center h-8 w-8 items-center rounded-sm text-brand-800 hover:bg-brand-50"}>
            <FontAwesomeIcon icon={faPencil} />
          </div>
        </div>


        {/* E-mail */}
        <div className={'col-span-2'}>E-mail</div>
        <div className={'col-span-2'}>{employee.email ?? '_'}</div>
        <div className={'flex col-span-1 justify-end hover:cursor-pointer'} onClick={() => changeEmailModal.open()}>
          <div className={"flex justify-center h-8 w-8 items-center rounded-sm text-brand-800 hover:bg-brand-50"}>
            <FontAwesomeIcon icon={faPencil} />
          </div>
        </div>

        {/* Web toegang */}
        <div className={'col-span-2'}>Web toegang:</div>
        <div className={'flex col-span-2'}>
          {employee.enabled ? <div className={'px-2 py-0.5 bg-brand-100 rounded-full text-brand-800'}>Kan inloggen</div>
          : <div>Kan niet inloggen</div>}
        </div>

        {employee.enabled ? <div className={'flex col-span-1 justify-end hover:cursor-pointer'} onClick={() => disableLoginModal.open()}>
            <div className={"flex justify-center h-8 w-8 items-center rounded-sm text-red-600 hover:bg-red-50"}>
              <FontAwesomeIcon icon={faTimes} />
            </div>
          </div>
          : <div className={'flex col-span-1 justify-end hover:cursor-pointer'} onClick={() => activateLoginModal.open()}>
          <div className={"flex justify-center h-8 w-8 items-center rounded-sm text-brand-800 hover:bg-brand-50"}>
            <FontAwesomeIcon icon={faUserPlus}/>
          </div>
        </div>
        }

        {/* BSN */}
        <div className={'col-span-2'}>BSN</div>
        <div className={'col-span-2'}>{employee.metadata.isBsnSet ? '*********' : '-'}</div>
        <div className={'flex col-span-1 justify-end hover:cursor-pointer'} onClick={() => editEmployeeBsnModal.open()}>
          <div className={"flex justify-center h-8 w-8 items-center rounded-sm text-brand-800 hover:bg-brand-50"}>
            <FontAwesomeIcon icon={faPencil} />
          </div>
        </div>

        {/* Contract start */}
        <div className={'col-span-2'}>Startdatum:</div>
        <div className={'col-span-2'}>{employee.metadata.contractStartDate ? moment(employee.metadata.contractStartDate).format('DD-MM-YYYY') : '-'}</div>
        <div className={'flex col-span-1 justify-end hover:cursor-pointer'} onClick={() => editContractPeriodModal.open()}>
          <div className={"flex justify-center h-8 w-8 items-center rounded-sm text-brand-800 hover:bg-brand-50"}>
            <FontAwesomeIcon icon={faPencil} />
          </div>
        </div>

        {/* Contract einde */}
        <div className={'col-span-2'}>Einddatum:</div>
        <div className={'col-span-2'}>{employee.metadata.contractEndDate ? moment(employee.metadata.contractEndDate).format('DD-MM-YYYY') : '-'}</div>
        <div className={'col-span-1'}></div>

        {/* Type werknememer */}
        <div className={'col-span-2'}>Type:</div>
        <div className={'col-span-2'}>{employee.metadata.getEmployeeTypeName()}</div>
        <div className={'flex col-span-1 justify-end hover:cursor-pointer'} onClick={() => editEmployeeTypeModal.open()}>
          <div className={"flex justify-center h-8 w-8 items-center rounded-sm text-brand-800 hover:bg-brand-50"}>
            <FontAwesomeIcon icon={faPencil} />
          </div>
        </div>

        {/* Rollen */}
        <div className={'col-span-2'}>Rollen:</div>
        <div className={'col-span-2 flex flex-row space-x-4 '}>
          {employee.roles.map((role, i) => {
            return <div key={i} className={'text-xs uppercase tracking-wide inline-block px-1 rounded-sm border border-brand-300 text-brand-400'}>{role}</div>
          })}
        </div>
        {canEditEmployeesRoles && <div className={'flex col-span-1 justify-end hover:cursor-pointer'} onClick={() => editEmployeeRoles.open()}>
          <div className={"flex justify-center h-8 w-8 items-center rounded-sm text-brand-800 hover:bg-brand-50"}>
            <FontAwesomeIcon icon={faPencil}/>
          </div>
        </div>}
      </div>
    </Card>
  </div>

}

const EmployeeSettings: FC<{ employee: Employee }> = ({ employee}) => {
  const {updateEmployeeMetadata} = useApi()
  const [hasTravelCompensation, setHasTravelCompensation] = useState<boolean>(employee.metadata.hasTravelCompensation ?? false)
  const [hasAllInSalary, setHasAllInSalary] = useState<boolean>(employee.metadata.hasAllInSalary)
  const [includeInHoursExport, setIncludeInHoursExport] = useState<boolean>(employee.metadata.includeInHoursExport)
  const [shadowEmployee, setShadowEmployee] = useState<boolean>(employee.metadata.shadowEmployee)
  const [errors, setErrors] = React.useState<string[]>([])

  const update = async () => {
    await updateEmployeeMetadata(employee.id, undefined, undefined, undefined, undefined, hasTravelCompensation, hasAllInSalary, includeInHoursExport, undefined, shadowEmployee)
      .catch(e => {
        if (e instanceof ValidationError) {
          setErrors(Object.values(e.errors).flat())
        }
        throw e
      })
  }

  return <div className={'-my-8'}>
    <Card title={'Algemene Instellingen'}>
      <div className={'grid grid-cols-5 gap-4 items-start'}>
        {/* Reiskosten */}
        <div className={'col-span-2 flex items-start'}>Instellingen:</div>
        <div className={'col-span-3 flex flex-col space-y-3'}>
          <Toggle label={'Reiskosten'} value={hasTravelCompensation} onChange={() => {
            setHasTravelCompensation(!hasTravelCompensation)
          }} size={'sm'} />
          <Toggle label={'All-in salaris'} value={hasAllInSalary} onChange={() => {
            setHasAllInSalary(!hasAllInSalary)
          }} size={'sm'} />
          <Toggle label={'Man-dagen staat'} value={includeInHoursExport} onChange={() => {
            setIncludeInHoursExport(!includeInHoursExport)
          }} size={'sm'} />
          <Toggle label={'Schaduw medewerker'} value={shadowEmployee} onChange={() => {
            setShadowEmployee(!shadowEmployee)
          }} size={'sm'} />
          { (
            hasTravelCompensation != employee.metadata.hasTravelCompensation
            || hasAllInSalary !== employee.metadata.hasAllInSalary
            || includeInHoursExport !== employee.metadata.includeInHoursExport
            || shadowEmployee !== employee.metadata.shadowEmployee
          ) && <div className={'mt-3'}>
            <Button type={'primary'} size={'sm'} text={'Wijzingen opslaan'} onClick={() => update()} />
          </div>}
        </div>
      </div>
    </Card>
  </div>
}

const EmployeeRooster: FC<{ employee: Employee }> = (props) => {
  return <div>
    <TabContainer title={'Rooster'} tabs={[
      {name: 'Basis', icon: faCalendar, body: <BasicWorkdayRoster employee={props.employee}/>},
      {name: 'Override', icon: faCalendar, body: <OverrideRoosterEditor employee={props.employee}/>}
    ]} />
  </div>
}

const BasicWorkdayRoster: FC<{ employee: Employee }> = (props) => {
  return <RoosterEditor employee={props.employee} />
}

const EmergencyDetailsCards: FC<{ employee: Employee }> = (props) => {
  return <div>
    <TabContainer title={'Noodgegevens'} tabs={[
      {name: 'Werknemer', icon: faHardHat, body: <EmployeeEmergencyCard employee={props.employee} />},
      {name: 'Contact', icon: faUser, body: <ContactEmergencyCard employee={props.employee} />},
      {name: 'Huisarts', icon: faStarOfLife, body: <GpEmergencyCard employee={props.employee} />}
    ]} />
  </div>
}

const EmployeePermissionEditor: FC<{ employee: Employee }> = (props) => {
  return <div className={'flex flex-col bg-white border border-slate-200 rounded-sm px-3 py-2'}>
    <h2 className="font-medium mb-2">Toegang</h2>
    <RoleEditor employee={props.employee}/>
  </div>
}

const PartialEmergencyDetailsBanner: FC = () => {
  return <div className={'flex flex-row space-x-3 col-span-2 items-center justify-center text-lg text-amber-800 bg-amber-300 py-4 border border-amber-600 rounded-sm'}>
    <div className="relative inline-flex items-center justify-center">
      <FontAwesomeIcon icon={faTriangleExclamation} className="absolute text-lg animate-ping text-amber-500"/>
      <FontAwesomeIcon icon={faTriangleExclamation} className="relative text-2xl"/>
    </div>
    <div>Er zijn nog openstaande velden voor de noodgegevens!</div>
    <div className="relative inline-flex items-center justify-center">
      <FontAwesomeIcon icon={faTriangleExclamation} className="absolute text-lg animate-ping text-amber-500"/>
      <FontAwesomeIcon icon={faTriangleExclamation} className="relative text-2xl"/>
    </div>
  </div>
}

const NoEmergencyDetailsBanner: FC = () => {
  return <div className={'flex flex-row space-x-3 col-span-2 items-center justify-center text-lg text-red-800 bg-red-300 py-4 border border-red-600 rounded-sm'}>
    <div className="relative inline-flex items-center justify-center">
      <FontAwesomeIcon icon={faTriangleExclamation} className="absolute text-lg animate-ping text-red-500"/>
      <FontAwesomeIcon icon={faTriangleExclamation} className="relative text-2xl"/>
    </div>
    <div>Let op! Er zijn nog geen noodgegevens bekend van deze werknemer!</div>
    <div className="relative inline-flex items-center justify-center">
      <FontAwesomeIcon icon={faTriangleExclamation} className="absolute text-lg animate-ping text-red-500"/>
      <FontAwesomeIcon icon={faTriangleExclamation} className="relative text-2xl"/>
    </div>
  </div>
}

